import * as Yup from 'yup';

const requiredField = (fieldName) => Yup.string().required(`${fieldName} is required`);
const emailValidation = Yup.string().email('Invalid email format').required('Email is required');
const phoneValidation = Yup.string().matches(/^[0-9]{10,15}$/, 'Invalid phone number format').required('Phone number is required');
const dateValidation = Yup.date().typeError('Must be a valid date').required('Date is required');

const positiveNumberValidation = Yup.number().typeError('Must be a number').positive('Must be a positive number').required('This field is required');

const optionalString = Yup.string();

const booleanValidation = Yup.boolean();

const ageValidation = Yup.number()
  .typeError('Age must be a number')
  .min(0, 'Age cannot be negative')
  .max(1800, 'Age must be reasonable')
  .required('Age is required');

const step1ValidationSchema = Yup.object().shape({
  property_details: Yup.object().shape({
    logo: optionalString,
    partnership_label: requiredField('Partnership Label'),
    landlord: requiredField('Landlord name'),
    utilities_available: Yup.object().shape({
      electricity: booleanValidation,
      heat: booleanValidation,
      water_waste: booleanValidation,
    }),
    pet_fees: Yup.object().shape({
      cat_fee: Yup.string().required('Cat fee is required'),
      dog_fee: Yup.string().required('Dog fee is required'),
      other_fee: Yup.string().required('Other pet fee is required'),
    }),
    allowed_pets: Yup.object().shape({
      dogs: Yup.boolean().test(
        'dogs-required-if-dog-pets',
        'Dogs must be allowed when dog pets are added',
        function (value) {
          const rootData = this.options.from?.[this.options.from.length - 1]?.value || {};
          const pets = rootData?.lease_details?.pets || [];
          const hasDogs = pets.some(pet =>
            pet.category && pet.category.toLowerCase().includes('dog')
          );
          return !hasDogs || value === true;
        }
      ),
      cats: Yup.boolean().test(
        'cats-required-if-cat-pets',
        'Cats must be allowed when cat pets are added',
        function (value) {
          const rootData = this.options.from?.[this.options.from.length - 1]?.value || {};
          const pets = rootData?.lease_details?.pets || [];
          const hasCats = pets.some(pet =>
            pet.category && pet.category.toLowerCase().includes('cat')
          );
          return !hasCats || value === true;
        }
      ),
      others: Yup.boolean().test(
        'others-required-if-other-pets',
        'Other pets must be allowed when other pet types are added',
        function (value) {
          const rootData = this.options.from?.[this.options.from.length - 1]?.value || {};
          const pets = rootData?.lease_details?.pets || [];
          const hasOthers = pets.some(pet =>
            pet.category &&
            !pet.category.toLowerCase().includes('dog') &&
            !pet.category.toLowerCase().includes('cat') &&
            pet.category.trim() !== ''
          );
          return !hasOthers || value === true;
        }
      ),
    }),
    property_name: requiredField('Property name'),
    building_name: requiredField('Building name'),
    unit_no: requiredField('Unit number'),
    address: requiredField('Address'),
    city: requiredField('City'),
    province: requiredField('Province'),
    postal_code: requiredField('Postal code'),
    appliances: Yup.array()
      .of(Yup.string().required('Each appliance must be a string'))
      .min(1, 'At least one appliance is required')
      .required('Appliances are required'),
  }),

  manager: Yup.object().shape({
    first_name: requiredField('Manager first name'),
    last_name: requiredField('Manager last name'),
    phone_no: phoneValidation,
    email: emailValidation,
    signature: optionalString,
    date: Yup.string().required('Manager signature date is required'),
  }),

  tenants: Yup.array().of(
    Yup.object().shape({
      id: optionalString,
      name: requiredField('Tenant name'),
      email: optionalString,
      signature: optionalString,
      date: optionalString,
    })
  ).min(1, 'At least one tenant is required'),

  occupants: Yup.array().of(
    Yup.object().shape({
      name: requiredField('Occupant name'),
      relationship: requiredField('Relationship to tenant'),
      age: ageValidation,
    })
  ),

  lease_details: Yup.object().shape({
    start_date: dateValidation,
    end_date: dateValidation.min(
      Yup.ref('start_date'),
      'End date cannot be before start date'
    ),
    basic_rent: positiveNumberValidation,
    pets: Yup.array()
      .of(
        Yup.object().shape({
          category: requiredField('Pet category'),
          total_fee: Yup.number()
            .typeError('Total Fee must be a number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Total Fee cannot be negative. Make sure you clicked on Done to save your changes.')
            .test(
              'fee-required-if-category',
              'Total Fee per pet is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const category = this.parent.category;
                return !category || category.length === 0 || value !== undefined;
              }
            ),
          no_of_pets: Yup.number()
            .typeError('Number must be a whole number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Cannot be negative. Make sure you clicked on Done to save your changes.')
            .integer('Must be a whole number. Make sure you clicked on Done to save your changes.')
            .test(
              'number-required-if-category',
              'Number of pets is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const category = this.parent.category;
                return !category || category.length === 0 || value !== undefined;
              }
            ),
        })
      )
      .test(
        'not-empty-objects',
        'Make sure you have entered the data in all fields and clicked on Done to save your changes.',
        (arr) => {
          if (!arr) return true;
          return arr.every((entry) =>
            entry.category || entry.total_fee !== undefined || entry.no_of_pets !== undefined
          );
        }
      ),

    parking: Yup.array()
      .of(
        Yup.object().shape({
          parking_type: requiredField('Parking type'),
          total_fee: Yup.number()
            .typeError('Total Fee must be a number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Total Fee cannot be negative. Make sure you clicked on Done to save your changes.')
            .test(
              'fee-required-if-parking_type',
              'Total Fee per parking slot is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const parkingType = this.parent.parking_type;
                return !parkingType || parkingType.length === 0 || value !== undefined;
              }
            ),
          no_of_slots: Yup.number()
            .typeError('Number must be a whole number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Cannot be negative. Make sure you clicked on Done to save your changes.')
            .integer('Must be a whole number. Make sure you clicked on Done to save your changes.')
            .test(
              'number-required-if-parking_type',
              'Number of parking slots is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const parkingType = this.parent.parking_type;
                return !parkingType || parkingType.length === 0 || value !== undefined;
              }
            ),
        })
      )
      .test(
        'not-empty-objects',
        'Make sure you have entered the data in all fields and clicked on Done to save your changes.',
        (arr) => {
          if (!arr) return true;
          return arr.every((entry) =>
            entry.parking_type || entry.total_fee !== undefined || entry.no_of_slots !== undefined
          );
        }
      ),

    storage: Yup.array()
      .of(
        Yup.object().shape({
          total_fee: Yup.number()
            .typeError('Storage Fee must be a number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Storage Fee cannot be negative. Make sure you clicked on Done to save your changes.')
            .required('Storage Fee per storage unit is required. Make sure you clicked on Done to save your changes.'),
          no_of_slots: Yup.number()
            .typeError('Number must be a whole number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Cannot be negative. Make sure you clicked on Done to save your changes.')
            .integer('Must be a whole number. Make sure you clicked on Done to save your changes.')
            .required('Number of storage slots is required. Make sure you clicked on Done to save your changes.'),
        })
      )
      .test(
        'not-empty-objects',
        'Make sure you have entered the data in all fields and clicked on Done to save your changes.',
        (arr) => {
          if (!arr) return true;
          return arr.every((entry) =>
            entry.total_fee !== undefined || entry.no_of_slots !== undefined
          );
        }
      ),
    pro_ration: Yup.object().shape({
      date_of_early_move_in: optionalString,
      amount_applicable: Yup.number()
        .typeError('Amount Applicable must be a number')
        .min(0, 'Amount Applicable cannot be negative')
        .test('amount-applicable-required-if-date', 'Amount Applicable is required', function (value) {
          const date = this.parent.date_of_early_move_in;
          return !date || date.length === 0 || value !== undefined;
        }),
      amount_to_be_charged: Yup.number()
        .typeError('Amount to be Charged must be a number')
        .min(0, 'Amount to be Charged cannot be negative')
        .test('amount-to-be-charged-required-if-date', 'Amount to be Charged is required', function (value) {
          const date = this.parent.date_of_early_move_in;
          return !date || date.length === 0 || value !== undefined;
        }),
    }),
    telus: Yup.object().shape({
      included: booleanValidation,
      charges: Yup.number()
        .typeError('Charges must be a number')
        .min(0, 'Charges cannot be negative')
        .test('charges-required-if-included', 'Telus charges are required', function (value) {
          const included = this.parent.included;
          return !included || value !== undefined;
        }),
    }),
    incentives: optionalString,
  }),

  security_deposits: Yup.object().shape({
    refundable: Yup.number()
      .typeError('Must be a number')
      .min(0, 'Deposit cannot be negative')
      .required('Refundable deposit amount is required'),
    non_refundable: Yup.number()
      .typeError('Must be a number')
      .min(0, 'Deposit cannot be negative')
      .required('Non-refundable deposit amount is required'),
    received_date: dateValidation
  }),

  attached_documents: Yup.array(),
  additional_notes: optionalString,
  isActive: booleanValidation,
});

const step2ValidationSchema = Yup.object().shape({
  attached_documents: Yup.array(),
  additional_notes: optionalString,
  isActive: booleanValidation,
});

const tenantEmailSchema = Yup.object().shape({
  tenants: Yup.array().of(
    Yup.object().shape({
      id: optionalString,
      name: requiredField('Tenant name'),
      email: emailValidation,
      signature: optionalString,
      date: Yup.date()
        .transform((value, originalValue) => {
          return originalValue === '' ? null : value;
        })
        .typeError('Must be a valid date')
        .nullable()
        .notRequired(),
    })
  ).min(1, 'At least one tenant is required'),
});

const step3ValidationSchema = Yup.object().shape({
  ...step2ValidationSchema.fields,
  tenants: tenantEmailSchema.fields.tenants
});

const step4ValidationSchema = Yup.object().shape({
  attached_documents: Yup.array().of(
    Yup.object().shape({
      id: requiredField('Document ID'),
      needsToBeUploaded: booleanValidation,
      url: Yup.string().test('url-required-if-not-uploaded', 'Document URL is required', function (value) {
        const needsToBeUploaded = this.parent.needsToBeUploaded;
        return needsToBeUploaded || (value && value.length > 0);
      }),
      originalUrl: optionalString,
      fileName: requiredField('Document name'),
      fileType: optionalString,
      toSendToUser: booleanValidation,
      isSignatureRequired: booleanValidation,
      tenantSignatures: Yup.array().test('signatures-required-if-needed', 'All tenants must sign this document', function (value) {
        const isUpload = this.parent.needsToBeUploaded;
        if (!isUpload) return true;

        if (!Array.isArray(value) || value.length === 0) return false;

        const allSignaturesValid = value.every(sig =>
          sig.tenantId && sig.tenantName && sig.signature && sig.date
        );
        if (!allSignaturesValid) return false;

        const { tenants } = this.options.context || {};
        if (!Array.isArray(tenants)) return true;

        const signedIds = value.map(sig => sig.tenantId);
        return tenants.every(t => signedIds.includes(t.id));
      }),
    })
  ),
  tenants: Yup.array().of(
    Yup.object().shape({
      id: requiredField('Tenant ID'),
      name: requiredField('Tenant name'),
      signature: requiredField('Signature is required'),
      date: dateValidation,
    })
  ).min(1, 'At least one tenant is required'),
});

const step5ValidationSchema = Yup.object().shape({
  isActive: Yup.boolean().required('isActive is required'),
});

export const validators = {
  step1: step1ValidationSchema,
  step2: step2ValidationSchema,
  step3: step3ValidationSchema,
  step4: step4ValidationSchema,
  step5: step5ValidationSchema,
}